// ---------------------------------------------------------
// Datei: diagnosticLog.ts
// Zweck: Leichtgewichtiger Ringpuffer für die letzten Konsolen-Fehler, den der Nutzer über den
// "Diagnose exportieren"-Button (SyncScreen) an den Support schicken kann, ohne dass Support
// physischen Gerätezugriff braucht (ADO #1231, Kriterium 7 - vom Ticket-Ersteller selbst als
// dringend markiert: "wir brauchen dringend eine Möglichkeit, dass User ... uns die Konsole so
// wie Netzwerktab usw schicken können, da es Remote schwierig ist die Fehler zu sehen").
// Kontext: Patcht console.error EINMALIG beim App-Start (initDiagnosticLog, siehe main.tsx),
// damit jeder bestehende console.error-Aufruf im gesamten Frontend automatisch mitprotokolliert
// wird, ohne jede einzelne Stelle im Code anzufassen. Persistiert zusätzlich (gekappt) in
// localStorage, damit Fehler, die VOR dem Öffnen des Diagnose-Screens auftreten (z.B. beim
// App-Start oder während eines Hintergrund-Syncs), nicht mit einem Reload verloren gehen.
// Stand: 2026-09-04
// ---------------------------------------------------------

export interface DiagnosticLogEntry {
  timestamp: string;
  message: string;
}

const MAX_ENTRIES = 50;
const STORAGE_KEY = "360volt-diagnostic-log";

const readStoredEntries = (): DiagnosticLogEntry[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

let entries: DiagnosticLogEntry[] = readStoredEntries();

const persist = (): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Ein Persistenzfehler des Diagnose-Logs selbst (z.B. Speicherplatz voll, Private Mode)
    // darf die App nicht stören - das Log bleibt dann nur für die laufende Session im Speicher.
  }
};

const formatArg = (arg: unknown): string => {
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  if (typeof arg === "string") return arg;
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
};

let patched = false;

/**
 * Muss GENAU EINMAL beim App-Start aufgerufen werden (siehe main.tsx). Ein wiederholter Aufruf
 * ist absichtlich ein No-Op (`patched`-Guard) - React StrictMode/HMR könnten das Modul sonst
 * mehrfach initialisieren und console.error mehrfach verschachtelt umschließen, wodurch jeder
 * spätere Fehler mehrfach protokolliert würde.
 */
export const initDiagnosticLog = (): void => {
  if (patched || typeof console === "undefined") return;
  patched = true;
  const originalError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    originalError(...args);
    entries.push({
      timestamp: new Date().toISOString(),
      message: args.map(formatArg).join(" "),
    });
    if (entries.length > MAX_ENTRIES) {
      entries = entries.slice(entries.length - MAX_ENTRIES);
    }
    persist();
  };
};

export const getDiagnosticLogEntries = (): DiagnosticLogEntry[] => [...entries];

/**
 * Leert den Ringpuffer und den persistierten localStorage-Eintrag. Muss bei Logout/
 * Session-Reset aufgerufen werden (siehe AuthContext.logout/handleUnauthorized) - `entries`
 * ist global und nicht an eine Benutzeridentität gebunden, auf einem gemeinsam genutzten
 * Monteur-Tablet könnte sich sonst Benutzer B anmelden und Diagnosedaten (inkl. dealName,
 * Projekt-/Phasen-IDs) aus der Sitzung von Benutzer A exportieren (#1231-Review-Finding D).
 */
export const clearDiagnosticLog = (): void => {
  entries = [];
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Siehe persist() oben - Speicherzugriff kann in Private Mode fehlschlagen.
  }
};
